import React, { useCallback, useId, useMemo, useState } from "react";

import {
  MAX_PRODUCT_MEDIA,
  buildMediaGroups,
  fill,
  getColorCoverEntry,
  getColorsWithoutPhotos,
  getCoverImage,
  getMediaStats,
  isFilledMedia,
  mediaCanCoverColor,
} from "../studioEditModel";
import {
  Card,
  Chip,
  ConfirmDialog,
  EmptyState,
  Icon,
  Menu,
  Notice,
  Swatch,
  Thumb,
  cx,
} from "../StudioUi";
import ImageDetailsDrawer from "../drawers/ImageDetailsDrawer";
import { mediaDraftActions } from "../mediaDraftActions";

/** Hidden file input + visible trigger; used for every upload entry point. */
const UploadTrigger = ({ id, disabled, onFiles, children, className }) => (
  <>
    <input
      id={id}
      type="file"
      accept="image/jpeg,image/png,image/webp"
      multiple
      className="pse-visually-hidden"
      disabled={disabled}
      onChange={(event) => {
        onFiles(event.target.files);
        event.target.value = "";
      }}
    />
    <label htmlFor={id} className={cx(className, disabled && "is-disabled")} aria-disabled={disabled}>
      {children}
    </label>
  </>
);

const MediaTile = ({ entry, color, studio, onOpen, onRemove, total }) => {
  const { tt, productColors } = studio;
  const { item, index } = entry;
  const isColorCover = color ? (item.colorCoverOptionValueIds || []).includes(color.optionValueId) : false;
  const coverColors = productColors.filter((entryColor) =>
    (item.colorCoverOptionValueIds || []).includes(entryColor.optionValueId)
  );
  const canCoverThisColor = color ? mediaCanCoverColor(item, color) : false;

  return (
    <div className={cx("pse-tile", item.isCover && "is-cover", isColorCover && "is-color-cover")}>
      <button type="button" className="pse-tile__image" onClick={() => onOpen(index)} aria-label={tt("mdOpenImage")}>
        <Thumb src={item.sourceUrl} alt={item.altText || ""} size="100%" fallbackLabel={tt("mdPreviewUnavailable")} />
      </button>
      <div className="pse-tile__badges">
        {item.isCover ? (
          <span className="pse-tile__badge pse-tile__badge--cover" title={tt("mdProductCoverLong")}>
            <Icon name="star" size={12} strokeWidth={2.4} />
            {tt("mdProductCover")}
          </span>
        ) : null}
        {color && isColorCover ? (
          <span className="pse-tile__badge pse-tile__badge--color" title={fill(tt("mdColorCoverFor"), { color: color.value })}>
            <Swatch value={color.value} colorRecords={studio.colorRecords} size={10} />
            {tt("mdColorCover")}
          </span>
        ) : null}
        {!color && coverColors.length ? (
          <span className="pse-tile__badge pse-tile__badge--color">
            {fill(tt("mdColorCoverFor"), { color: coverColors.map((entryColor) => entryColor.value).join(", ") })}
          </span>
        ) : null}
      </div>
      <span className="pse-tile__position" aria-hidden="true">
        {index + 1}
      </span>
      <div className="pse-tile__menu">
        <Menu
          label={tt("mdImageActions")}
          items={[
            { label: tt("mdOpenImage"), icon: "edit", onClick: () => onOpen(index) },
            !item.isCover
              ? {
                  label: tt("mdSetProductCover"),
                  icon: "star",
                  onClick: () => studio.setMediaDrafts((items) => mediaDraftActions.setProductCover(items, index)),
                }
              : null,
            color && !isColorCover
              ? {
                  label: tt("mdSetColorCover"),
                  icon: "sparkle",
                  disabled: !canCoverThisColor,
                  title: canCoverThisColor ? undefined : fill(tt("mdColorCoverIneligible"), { color: color.value }),
                  onClick: () =>
                    studio.setMediaDrafts((items) =>
                      mediaDraftActions.toggleColorCover(items, index, color.optionValueId, true)
                    ),
                }
              : null,
            index > 0
              ? {
                  label: tt("moveEarlier"),
                  icon: "chevronLeft",
                  onClick: () => studio.setMediaDrafts((items) => mediaDraftActions.move(items, index, -1)),
                }
              : null,
            index < total - 1
              ? {
                  label: tt("moveLater"),
                  icon: "chevronRight",
                  onClick: () => studio.setMediaDrafts((items) => mediaDraftActions.move(items, index, 1)),
                }
              : null,
            { label: tt("mdRemovePhoto"), icon: "trash", tone: "danger", onClick: () => onRemove(index) },
          ]}
        />
      </div>
    </div>
  );
};

const MediaTab = ({ studio }) => {
  const { tt, mediaDrafts, productColors, colorRecords, dirty, isUploadingMedia, handleMediaUpload, setMediaDrafts } =
    studio;
  const ids = useId();
  const [openIndex, setOpenIndex] = useState(null);
  const [removeIndex, setRemoveIndex] = useState(null);

  const filled = useMemo(() => mediaDrafts.filter(isFilledMedia), [mediaDrafts]);
  const groups = useMemo(() => buildMediaGroups(mediaDrafts, productColors), [mediaDrafts, productColors]);
  const stats = getMediaStats(mediaDrafts, productColors);
  const cover = getCoverImage(mediaDrafts);
  const left = Math.max(0, MAX_PRODUCT_MEDIA - filled.length);
  const limitReached = left === 0;
  const colorsWithout = getColorsWithoutPhotos(mediaDrafts, productColors);
  const uploadDisabled = isUploadingMedia || limitReached;

  const handleRemoveConfirmed = useCallback(() => {
    const index = removeIndex;
    setRemoveIndex(null);
    setOpenIndex(null);
    setMediaDrafts((items) => mediaDraftActions.remove(items, index));
  }, [removeIndex, setMediaDrafts]);

  const uploadLabel = (
    <>
      <Icon name={isUploadingMedia ? "refresh" : "upload"} size={17} className={isUploadingMedia ? "pse-spin" : ""} />
      <span>{isUploadingMedia ? tt("mdUploading") : tt("mdUpload")}</span>
    </>
  );

  const renderGroup = (group) => {
    const color = group.color;
    const coverEntry = color ? getColorCoverEntry(mediaDrafts, color) : null;
    const title = color ? color.value : group.shared ? tt("mdSharedGroup") : tt("mdAllPhotos");
    const uploadVariantIds = color ? color.variantIds : [];
    return (
      <section key={group.key} className="pse-card pse-media-group" aria-label={title}>
        <header className="pse-media-group__head">
          <div className="pse-media-group__title">
            {color ? (
              <Swatch value={color.value} colorRecords={colorRecords} size={26} />
            ) : (
              <span className="pse-media-group__icon">
                <Icon name={group.shared ? "layers" : "image"} size={18} />
              </span>
            )}
            <h3>{title}</h3>
            <Chip>{fill(tt("mdPhotosCount"), { count: group.entries.length })}</Chip>
          </div>
          {color ? (
            <span className={cx("pse-media-group__cover", !coverEntry && "is-auto")}>
              {coverEntry ? (
                <>
                  <Icon name="checkCircle" size={16} />
                  {fill(tt("mdColorCoverFor"), { color: color.value })}
                </>
              ) : (
                tt("mdColorCoverAuto")
              )}
            </span>
          ) : group.shared ? (
            <span className="pse-media-group__cover is-auto">{tt("mdSharedGroupHint")}</span>
          ) : null}
        </header>
        <div className="pse-tile-grid">
          {group.entries.map((entry) => (
            <MediaTile
              key={entry.item.id || entry.item.storageKey || entry.index}
              entry={entry}
              color={color}
              studio={studio}
              total={mediaDrafts.length}
              onOpen={setOpenIndex}
              onRemove={setRemoveIndex}
            />
          ))}
          <UploadTrigger
            id={`${ids}-upload-${group.key}`}
            disabled={uploadDisabled}
            onFiles={(files) => handleMediaUpload(files, { variantIds: uploadVariantIds })}
            className="pse-tile pse-tile--upload"
          >
            <Icon name="plus" size={22} />
            <strong>{tt("mdUploadTile")}</strong>
            <small>{tt("mdUploadFormats")}</small>
            <small>
              {limitReached
                ? fill(tt("mdLimitReached"), { max: MAX_PRODUCT_MEDIA })
                : fill(tt("mdLimitLeft"), { count: left })}
            </small>
          </UploadTrigger>
        </div>
        {color && !group.entries.length ? <p className="pse-muted pse-small">{tt("mdGroupEmpty")}</p> : null}
      </section>
    );
  };

  return (
    <div className="pse-layout">
      <div className="pse-layout__main">
        <Card
          title={tt("mdTitle")}
          subtitle={tt("mdCopy")}
          actions={
            <>
              {dirty.media ? <Chip tone="warning">{tt("unsavedChip")}</Chip> : null}
              <UploadTrigger
                id={`${ids}-upload-main`}
                disabled={uploadDisabled}
                onFiles={(files) => handleMediaUpload(files)}
                className="pse-btn pse-btn--primary pse-btn--md"
              >
                {uploadLabel}
              </UploadTrigger>
            </>
          }
        >
          {limitReached ? (
            <Notice tone="warning">{fill(tt("mdLimitReached"), { max: MAX_PRODUCT_MEDIA })}</Notice>
          ) : null}
          {!filled.length ? (
            <EmptyState
              icon="image"
              title={tt("mdEmptyTitle")}
              action={
                <UploadTrigger
                  id={`${ids}-upload-empty`}
                  disabled={uploadDisabled}
                  onFiles={(files) => handleMediaUpload(files)}
                  className="pse-btn pse-btn--primary pse-btn--lg"
                >
                  {uploadLabel}
                </UploadTrigger>
              }
            >
              {tt("mdEmptyCopy")}
            </EmptyState>
          ) : null}
        </Card>

        {filled.length || productColors.length ? groups.map(renderGroup) : null}

        {filled.length > 1 ? (
          <Card title={tt("mdOrderTitle")} subtitle={tt("mdOrderCopy")}>
            <ol className="pse-order-strip">
              {mediaDrafts.map((item, index) =>
                isFilledMedia(item) ? (
                  <li key={item.id || item.storageKey || index} className="pse-order-strip__item">
                    <button type="button" className="pse-order-strip__image" onClick={() => setOpenIndex(index)}>
                      <Thumb src={item.sourceUrl} size={64} alt="" />
                      {item.isCover ? (
                        <span className="pse-order-strip__star" title={tt("mdProductCoverLong")}>
                          <Icon name="star" size={11} strokeWidth={2.6} />
                        </span>
                      ) : null}
                    </button>
                    <span className="pse-order-strip__number">{index + 1}</span>
                    <div className="pse-order-strip__controls">
                      <button
                        type="button"
                        className="pse-icon-btn pse-icon-btn--sm"
                        aria-label={tt("moveEarlier")}
                        disabled={index === 0}
                        onClick={() => setMediaDrafts((items) => mediaDraftActions.move(items, index, -1))}
                      >
                        <Icon name="chevronLeft" size={15} />
                      </button>
                      <button
                        type="button"
                        className="pse-icon-btn pse-icon-btn--sm"
                        aria-label={tt("moveLater")}
                        disabled={index === mediaDrafts.length - 1}
                        onClick={() => setMediaDrafts((items) => mediaDraftActions.move(items, index, 1))}
                      >
                        <Icon name="chevronRight" size={15} />
                      </button>
                    </div>
                  </li>
                ) : null
              )}
            </ol>
          </Card>
        ) : null}
      </div>

      <aside className="pse-layout__side">
        <Card title={tt("mdPreview")}>
          <div className="pse-preview-hero">
            {cover?.sourceUrl ? (
              <img src={cover.sourceUrl} alt={cover.altText || ""} />
            ) : (
              <span className="pse-preview-hero__empty">
                <Icon name="image" size={32} />
                {tt("noImage")}
              </span>
            )}
          </div>
          {filled.length > 1 ? (
            <div className="pse-preview-strip">
              {filled.slice(0, 5).map((item, index) => (
                <Thumb
                  key={item.id || item.storageKey || index}
                  src={item.sourceUrl}
                  size={50}
                  className={item === cover ? "is-active" : ""}
                />
              ))}
            </div>
          ) : null}
        </Card>

        <Card title={tt("mdStats")}>
          <div className="pse-kv-list">
            <div className="pse-kv">
              <span className="pse-kv__label">
                <Icon name="image" size={17} className="pse-kv__icon" />
                {tt("mdStatTotal")}
              </span>
              <span className="pse-kv__value">
                {stats.total} / {MAX_PRODUCT_MEDIA}
              </span>
            </div>
            {productColors.length ? (
              <>
                <div className="pse-kv">
                  <span className="pse-kv__label">
                    <Icon name="variants" size={17} className="pse-kv__icon" />
                    {tt("mdStatColors")}
                  </span>
                  <span className="pse-kv__value">
                    {stats.colorsWithPhotos} / {stats.colorsTotal}
                  </span>
                </div>
                <div className="pse-kv">
                  <span className="pse-kv__label">
                    <Icon name="sparkle" size={17} className="pse-kv__icon" />
                    {tt("mdStatCovers")}
                  </span>
                  <span className="pse-kv__value">
                    {stats.colorsWithCover} / {stats.colorsTotal}
                  </span>
                </div>
              </>
            ) : null}
            <div className="pse-kv">
              <span className="pse-kv__label">
                <Icon name="details" size={17} className="pse-kv__icon" />
                {tt("mdStatAlt")}
              </span>
              <span className="pse-kv__value">
                {stats.withAlt} / {stats.total}
              </span>
            </div>
            <div className="pse-kv">
              <span className="pse-kv__label">
                <Icon name="upload" size={17} className="pse-kv__icon" />
                {tt("mdStatFormats")}
              </span>
              <span className="pse-kv__value">JPG, PNG, WEBP</span>
            </div>
          </div>
        </Card>

        {filled.length ? (
          colorsWithout.length ? (
            <Notice tone="warning" title={tt("mdReadyMissing")}>
              {fill(tt("mdReadyMissingCopy"), { colors: colorsWithout.map((color) => color.value).join(", ") })}
            </Notice>
          ) : (
            <Notice tone="success" title={tt("mdReadyAll")}>
              {productColors.length ? tt("mdReadyAllCopy") : null}
            </Notice>
          )
        ) : null}
        {stats.total && stats.withAlt < stats.total ? (
          <Notice tone="info" title={tt("mdHintsTitle")}>
            {fill(tt("mdHintAltMany"), { count: stats.total - stats.withAlt })}
          </Notice>
        ) : null}
      </aside>

      <ImageDetailsDrawer
        studio={studio}
        index={openIndex}
        onClose={() => setOpenIndex(null)}
        onNavigate={setOpenIndex}
        onRequestRemove={setRemoveIndex}
      />

      <ConfirmDialog
        isOpen={removeIndex !== null}
        title={tt("mdRemoveConfirmTitle")}
        confirmLabel={tt("mdRemovePhoto")}
        cancelLabel={tt("cancel")}
        tone="danger"
        onCancel={() => setRemoveIndex(null)}
        onConfirm={handleRemoveConfirmed}
      >
        <p>{tt("mdRemoveConfirmCopy")}</p>
        {removeIndex !== null && mediaDrafts[removeIndex]?.isCover ? <p>{tt("mdRemoveConfirmCover")}</p> : null}
      </ConfirmDialog>
    </div>
  );
};

export default MediaTab;
